import { can } from "../../../helpers/permissions";
import { canAccessScreen } from "../../../helpers/screenGuards";
import { ASHLEY_PROMPTS } from "../../speech/ashleyPrompts";
import { VOICE_INTENT_CATEGORY } from "../../ashleyInterfaceTypes";
import { evaluatePreconditions } from "../intents/voiceIntentPreconditions";
import {
  getVoiceIntentById,
  HANDLER_TARGET,
  SCREEN_TARGET,
} from "../intents/voiceIntentRegistry";
import { VOICE_WORKFLOW } from "../../ashleyInterfaceTypes";
import { deferredVoiceFailure, deferredVoiceResult } from "./deferredVoiceAction";
import { EmailService } from "../../../connectedServices";
import { createGmailConversationState } from "../../../connectedServices/email/gmailConversationState";

/**
 * @typedef {object} DispatchResult
 * @property {boolean} success
 * @property {string} [message]
 * @property {string} [code]
 * @property {() => void} [performAction] Runs after acknowledgement speech completes.
 */

/**
 * @param {object} appDispatch
 */
export function createVoiceRouter(appDispatch) {
  return async function dispatchVoiceIntent(resolvedIntent) {
    const intent = getVoiceIntentById(resolvedIntent.intentId);
    if (!intent) {
      return {
        success: false,
        code: "unknown_intent",
        message: ASHLEY_PROMPTS.UNKNOWN_COMMAND,
      };
    }

    const actor = appDispatch.getActor();
    const sessionContext = appDispatch.getSessionContext();

    if (intent.capability && !can(actor, intent.capability, {}, appDispatch.getOrgPolicies())) {
      return {
        success: false,
        code: "access_denied",
        message: intent.feedback?.denied || ASHLEY_PROMPTS.ACCESS_DENIED,
      };
    }

    if (intent.target?.type === "screen") {
      if (!canAccessScreen(actor, intent.target.screen, {}, appDispatch.getOrgPolicies())) {
        return {
          success: false,
          code: "access_denied",
          message: intent.feedback?.denied || ASHLEY_PROMPTS.ACCESS_DENIED,
        };
      }
    }

    if (intent.category === VOICE_INTENT_CATEGORY.ACTION) {
      const preconditionResult = evaluatePreconditions(intent.preconditions, sessionContext);
      if (!preconditionResult.ok) {
        return {
          success: false,
          code: preconditionResult.code,
          message: preconditionResult.message,
        };
      }
    }

    if (intent.category === VOICE_INTENT_CATEGORY.WORKFLOW) {
      const workflow = intent.target?.workflow;
      const screen = intent.target?.screen;

      if (workflow === VOICE_WORKFLOW.OPEN_MATERIALS_PICKER) {
        if (!sessionContext.activeSession) {
          return deferredVoiceFailure(
            intent.feedback?.failed || ASHLEY_PROMPTS.NO_ACTIVE_SESSION_MATERIALS,
            () => appDispatch.navigateToScreen(SCREEN_TARGET.TIME_TRACKING_V2),
            "no_active_session",
          );
        }

        return deferredVoiceResult(
          intent.feedback?.success || "Opening materials.",
          () => appDispatch.launchWorkflow({ screen, workflow }),
        );
      }

      if (workflow === VOICE_WORKFLOW.OPEN_STOP_SESSION) {
        if (!sessionContext.activeSession) {
          return deferredVoiceFailure(
            intent.feedback?.failed || ASHLEY_PROMPTS.NO_ACTIVE_SESSION,
            () => appDispatch.navigateToScreen(SCREEN_TARGET.TIME_TRACKING_V2),
            "no_active_session",
          );
        }

        return deferredVoiceResult(
          intent.feedback?.success || "Opening stop session.",
          () => appDispatch.launchWorkflow({ screen, workflow }),
        );
      }

      if (workflow === VOICE_WORKFLOW.OPEN_START_SESSION) {
        return deferredVoiceResult(
          intent.feedback?.success || "Opening start session.",
          () => appDispatch.openStartSessionGuided(),
        );
      }
    }

    if (intent.category === VOICE_INTENT_CATEGORY.NAVIGATION) {
      if (intent.target.type === "screen") {
        return deferredVoiceResult(
          intent.feedback?.success || `Opening ${intent.label}.`,
          () =>
            appDispatch.navigateToScreen(intent.target.screen, {
              section: intent.target.section,
            }),
        );
      }

      if (intent.target.type === "handler") {
        const performAction = () => {
          if (intent.target.handler === HANDLER_TARGET.OPEN_PROJECTS_HUB) {
            appDispatch.openProjectsHub();
          } else if (intent.target.handler === HANDLER_TARGET.OPEN_NEW_PROJECT) {
            appDispatch.openNewProjectGuided();
          } else if (intent.target.handler === HANDLER_TARGET.OPEN_CLIENTS_HUB) {
            appDispatch.openClientsHub();
          } else if (intent.target.handler === HANDLER_TARGET.OPEN_NEW_CLIENT) {
            appDispatch.openNewClient();
          } else if (intent.target.handler === HANDLER_TARGET.OPEN_ESTIMATE_HUB) {
            appDispatch.openEstimateHub();
          } else if (intent.target.handler === HANDLER_TARGET.OPEN_NEW_ESTIMATE) {
            appDispatch.openNewEstimate();
          }
        };

        return deferredVoiceResult(
          intent.feedback?.success || `Opening ${intent.label}.`,
          performAction,
        );
      }
    }

    if (intent.category === VOICE_INTENT_CATEGORY.ACTION) {
      if (intent.target.handler === HANDLER_TARGET.START_BREAK) {
        const result = await appDispatch.startBreak();
        if (!result.success) {
          return result;
        }
        return {
          success: true,
          message: intent.feedback?.success || ASHLEY_PROMPTS.START_BREAK_SUCCESS,
        };
      }

      if (intent.target.handler === HANDLER_TARGET.STOP_BREAK) {
        const result = await appDispatch.stopBreak();
        if (!result.success) {
          return result;
        }
        return {
          success: true,
          message: intent.feedback?.success || ASHLEY_PROMPTS.STOP_BREAK_SUCCESS,
        };
      }

      if (intent.target.handler === HANDLER_TARGET.SIGN_OUT) {
        return deferredVoiceResult(
          intent.feedback?.success || "Signing out. Have a wonderful day.",
          () => appDispatch.signOut(),
        );
      }

      if (intent.target.handler === HANDLER_TARGET.CHECK_RECENT_EMAILS) {
        const result = await EmailService.summarizeRecent({ hours: 72 });
        if (!result.success) {
          return {
            success: false,
            code: result.code || "email_not_connected",
            message: result.message || intent.feedback?.failed,
          };
        }

        const count = typeof result.count === "number" ? result.count : 0;
        if (count <= 0) {
          return {
            success: true,
            message: result.message,
            awaitFollowUp: "open_email_inbox",
          };
        }

        return {
          success: true,
          message: result.message,
          awaitFollowUp: "email_browse",
          followUpContext: createGmailConversationState({
            messages: Array.isArray(result.messages) ? result.messages : [],
            offset: 0,
            pageSize: 5,
            afterSummary: false,
            browsingMode: "browse",
          }),
        };
      }

      if (intent.target.handler === HANDLER_TARGET.OPEN_EMAIL_INBOX) {
        return EmailService.openInbox();
      }

      if (intent.target.handler === HANDLER_TARGET.CHECK_SPAM_ATTENTION) {
        const result = await EmailService.checkSpamAttention();
        if (!result.success) {
          return {
            success: false,
            code: result.code || "email_not_connected",
            message: result.message || intent.feedback?.failed,
          };
        }

        const count = typeof result.count === "number" ? result.count : 0;
        if (count <= 0) {
          return {
            success: true,
            message: result.message,
          };
        }

        return {
          success: true,
          message: result.message,
          awaitFollowUp: "spam_attention",
          followUpContext: {
            messages: Array.isArray(result.messages) ? result.messages : [],
          },
        };
      }

      if (intent.target.handler === HANDLER_TARGET.REVIEW_ALL_UNREAD_EMAILS) {
        const result = await EmailService.reviewAllUnread({ hours: 72 });
        if (!result.success) {
          return {
            success: false,
            code: result.code || "email_not_connected",
            message: result.message || intent.feedback?.failed,
          };
        }

        const count = typeof result.count === "number" ? result.count : 0;
        if (count <= 0) {
          return {
            success: true,
            message: result.message,
            awaitFollowUp: "open_email_inbox",
          };
        }

        return {
          success: true,
          message: result.message,
          awaitFollowUp: "email_review_all",
          followUpContext: createGmailConversationState({
            messages: Array.isArray(result.messages) ? result.messages : [],
            afterSummary: false,
            browsingMode: "review_all",
          }),
        };
      }

      if (intent.target.handler === HANDLER_TARGET.LAUNCH_EXECUTIVE_BRIEFING) {
        return deferredVoiceResult(
          intent.feedback?.success || ASHLEY_PROMPTS.EXECUTIVE_BRIEFING_START,
          () => {
            void appDispatch.launchExecutiveBriefing();
          },
        );
      }
    }

    return {
      success: false,
      code: "unhandled_intent",
      message: ASHLEY_PROMPTS.UNKNOWN_COMMAND,
    };
  };
}
